import React, { useMemo, useContext, useState, useEffect } from 'react';
import ViewIcon from 'assets/open_in_new.svg?react';
import CwFPagination from 'components/CwFPagination';
import LoadingSpinner from 'components/LoadingSpinner';
import SortableTable from 'components/UiKit/SortableTable';
import { format } from 'date-fns';
import tableSort from 'helpers/tableSort';
import PropTypes from 'prop-types';
import { Link, useHistory, useParams } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { AmplitudeContext } from 'utilities/amplitude/useAmplitude';
import { paths } from 'routes/paths';
import useProducts from 'hooks/useProducts';
import useInventoryInvoiceData from 'screens/Inventory/helpers/useInventoryInvoiceData';
import useInventoryInvoiceTotalQuantity from 'screens/Inventory/helpers/useInventoryInvoiceTotalQuantity';
import { getAbbreviatedUnit } from 'helpers/unitsAbbreviation';
import routesForPPLToCreate from 'screens/ProductList/helpers/creationRoutes';
import { Maths } from 'helpers/maths';
import formatNumber from 'helpers/formatNumber';
import { getConvertUnitFromTo } from 'helpers/unitConversionHelpers';
import useUomConvert from 'hooks/useUomConvert';
import useInvoiceFlags from 'screens/Invoices/redesign/hooks/useInvoiceFlags';
import { SEASON_IDS_PARAM } from 'utilities/cropSeasonUrl';
import { PRODUCT_INVOICE_LOOKUP } from 'constants/productInvoiceLookupFlags';
import { seedCropSeasonsForSameTabNav } from '../ProductDetailTasksTable/components/TableColumns';
import ProductDetailTableEmptyState from '../ProductDetailTableEmptyState';
import './index.css';

const PAGE_COUNT = 20;
const INVOICE_SORT_FIELDS = {
  invoiceName: 'invNumber',
  invoiceDate: 'invoiceDate',
  productCost: 'productCost',
  unitPriceValue: 'unitPriceValue',
  invoiceQuantity: 'productQuantityValue'
};

const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
});

const formatUnitPrice = value =>
  Number.isFinite(value) ? formatter.format(value) : '—';

/**
 * The product view lists invoices across seasons, while the redesigned invoices
 * landing is season-scoped: without the invoice's own season the detail panel
 * opens on a record that is nowhere in the list beside it.
 *
 * With the redesign, the View link opens the landing filtered by invoice name
 * and ID; season(s) ride along in `?seasonIds`, which applyUrlCropSeasons
 * consumes before render. A same-tab click seeds them from the link's onClick.
 */
const buildInvoiceViewPath = (invoice, feInvoicesActive) => {
  if (!feInvoicesActive) {
    return { pathname: `${paths.invoices}/${invoice.id}/view` };
  }

  const cropSeasonIds = invoice.invoiceCropSeasonIds ?? [];
  const params = new URLSearchParams();
  if (invoice.invoiceName) {
    params.set('search', invoice.invoiceName);
  }
  if (invoice.id) {
    params.set('operationId', invoice.id);
  }
  if (cropSeasonIds.length) {
    params.set(SEASON_IDS_PARAM, cropSeasonIds.join(','));
  }

  return {
    pathname: paths.invoices,
    search: params.toString() ? `?${params.toString()}` : '',
    state: { operationId: invoice.id }
  };
};

const InvoiceActions = ({ invoice, feInvoicesActive }) => {
  const amplitude = useContext(AmplitudeContext);

  return (
    <div className="w-full flex flex-row justify-end">
      <Link
        to={buildInvoiceViewPath(invoice, feInvoicesActive)}
        className="product-invoice-table__view-link"
        onClick={event => {
          seedCropSeasonsForSameTabNav(
            event,
            invoice.invoiceCropSeasonIds,
            feInvoicesActive
          );
          amplitude.sendEventToAmplitude(
            amplitude.events.epic.ProductList.viewInvoice,
            { productId: invoice.id }
          );
        }}
      >
        <ViewIcon className="mr-1" role="img" aria-label="view icon" />
        <span>View</span>
      </Link>
    </div>
  );
};

InvoiceActions.propTypes = {
  invoice: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    invoiceCropSeasonIds: PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  feInvoicesActive: PropTypes.bool
};

const cellRenderers = {
  invoiceName: invoice => invoice.invoiceName,
  invoiceDate: invoice => format(new Date(invoice.invoiceDate), 'MMM dd, u'),
  productCost: invoice => formatter.format(invoice.productCost),
  unitPriceValue: invoice =>
    `${formatUnitPrice(invoice.unitPriceValue)} / ${getAbbreviatedUnit(
      invoice.unitPriceUnit || invoice.unit
    )}`,
  invoiceQuantity: invoice =>
    formatNumber(invoice.invoiceQuantity, getAbbreviatedUnit(invoice.unit)),
  actions: (invoice, feInvoicesActive) => (
    <InvoiceActions invoice={invoice} feInvoicesActive={feInvoicesActive} />
  )
};

const ProductDetailInvoiceTable = ({ cropSeasons, dateRange = null }) => {
  const convertFn = useUomConvert();
  const { id: productId } = useParams();
  const history = useHistory();
  const [page, setPage] = useState(0);
  const [sortField, setSortField] = useState('invoiceDate');
  const [sortDir, setSortDir] = useState('desc');
  const [
    filteredTotalInvoiceQuantity,
    setFilteredTotalInvoiceQuantity
  ] = useState(0);

  const { isLoading, getProduct, selectedProduct } = useProducts();
  const { feInvoicesActive } = useInvoiceFlags();
  const flags = useFlags();
  const useProductScopedLookup = flags[PRODUCT_INVOICE_LOOKUP] === true;
  const lookupSortField = useProductScopedLookup
    ? INVOICE_SORT_FIELDS[sortField]
    : undefined;
  const lookupSortDirection = lookupSortField ? sortDir : undefined;
  const { invoices, invsLoading } = useInventoryInvoiceData(
    productId,
    cropSeasons,
    useProductScopedLookup,
    lookupSortField,
    lookupSortDirection,
    dateRange
  );
  const {
    totalInvoicedQuantity,
    unit: totalInvoicedQuantityUnit
  } = useInventoryInvoiceTotalQuantity(
    productId,
    cropSeasons,
    dateRange,
    useProductScopedLookup
  );

  useEffect(() => {
    if (productId) {
      getProduct(productId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  // Reset to the first page whenever the filters change so the current page
  // doesn't fall out of range and strand the user on an empty page.
  useEffect(() => {
    setPage(0);
  }, [cropSeasons, dateRange, sortField, sortDir]);

  const { rows: invoiceRows } = useMemo(() => {
    if (!selectedProduct || !invoices)
      return { rows: [], totalProductCount: 0 };
    let productCount = 0;

    const invoiceData = invoices.map(invoice => {
      if (!useProductScopedLookup) {
        const productUses = (invoice.invoiceProducts ?? []).filter(
          product =>
            product.productId === selectedProduct.productId ||
            product.id === selectedProduct.productId
        );
        const productUseUnits = [
          ...new Set(
            productUses.map(({ totalQuantityUnit }) => totalQuantityUnit)
          )
        ];
        const invoiceQuantity = productUses.reduce(
          (total, product) =>
            Maths.plus(
              total,
              productUseUnits.length === 1
                ? product.totalQuantityValue
                : product.totalQuantityValueInStandardPackageUnit
            ).toNumber(),
          0
        );
        const productCost = productUses.reduce(
          (total, product) =>
            Maths.plus(total, product.totalCostValue).toNumber(),
          0
        );
        const invoiceCropSeasonIds = (invoice.cropSeasons ?? [])
          .map(season => season.seasonId)
          .filter(Boolean);
        const productUsePriceUnits = [
          ...new Set(
            productUses
              .map(({ unitPriceUnit }) => unitPriceUnit)
              .filter(Boolean)
          )
        ];
        const fallbackUnit =
          productUseUnits.length === 1
            ? productUseUnits[0]
            : selectedProduct.stdPackageUnit;
        const unitPriceUnit =
          productUsePriceUnits.length === 1
            ? productUsePriceUnits[0]
            : fallbackUnit;

        return {
          id: invoice.id,
          invoiceName: invoice.invoiceNumber,
          invoiceDate: invoice.invoiceDate,
          invoiceCropSeasonIds,
          productCost,
          unitPriceValue: Maths.divide(productCost, invoiceQuantity).toNumber(),
          invoiceQuantity,
          unitPriceUnit,
          unit:
            productUseUnits.length === 1
              ? productUseUnits[0]
              : selectedProduct.stdPackageUnit
        };
      }

      const invoiceQuantity = invoice.productQuantityValue;
      const { productCost } = invoice;
      productCount += invoiceQuantity;

      return {
        id: invoice.invoiceId,
        invoiceName: invoice.invNumber,
        invoiceDate: invoice.invoiceDate,
        invoiceCropSeasonIds: invoice.cropSeasonIds ?? [],
        productCost,
        unitPriceValue: invoice.unitPriceValue,
        invoiceQuantity,
        unitPriceUnit: invoice.unitPriceUnit,
        unit: invoice.productQuantityUnit
      };
    });
    return { rows: invoiceData, totalProductCount: productCount };
  }, [selectedProduct, invoices, useProductScopedLookup]);

  const filteredAndSortedInvoices = useMemo(() => {
    const filtered = invoiceRows.filter(row => {
      const invoiceDate = new Date(row.invoiceDate);
      const dateFilter =
        useProductScopedLookup ||
        !dateRange ||
        dateRange.length === 0 ||
        (dateRange[0] &&
          !dateRange[1] &&
          invoiceDate >= new Date(dateRange[0])) ||
        (dateRange[0] &&
          dateRange[1] &&
          invoiceDate >= new Date(dateRange[0]) &&
          invoiceDate <= new Date(dateRange[1]));
      const cropFilter =
        useProductScopedLookup ||
        !cropSeasons?.length ||
        row.invoiceCropSeasonIds.some(seasonId =>
          cropSeasons.includes(seasonId)
        );
      return dateFilter && cropFilter;
    });
    const sortDirection = sortDir === 'asc' ? 'ascend' : 'descend';
    return useProductScopedLookup && INVOICE_SORT_FIELDS[sortField]
      ? filtered
      : tableSort(filtered, sortField, sortDirection);
  }, [
    invoiceRows,
    sortField,
    sortDir,
    dateRange,
    cropSeasons,
    useProductScopedLookup
  ]);

  const TABLE_COLUMNS = [
    {
      field: 'invoiceName',
      sortable: true,
      title: `Invoice Name (${filteredAndSortedInvoices.length})`
    },
    {
      field: 'invoiceDate',
      sortable: true,
      title: 'Invoice Date'
    },
    {
      field: 'productCost',
      sortable: true,
      title: 'Total Cost'
    },
    {
      field: 'unitPriceValue',
      sortable: true,
      title: 'Cost per unit'
    },
    {
      field: 'invoiceQuantity',
      sortable: true,
      title: 'Invoice Quantity'
    },
    {
      field: 'actions',
      title: ''
    }
  ];

  const paginatedRows = filteredAndSortedInvoices.slice(
    page * PAGE_COUNT,
    (page + 1) * PAGE_COUNT
  );

  // Total Invoiced Quantity is a grand total across the entire filtered set
  // (all pages), so it is computed from filteredAndSortedInvoices rather than
  // the current page slice — this keeps it stable while paging.
  useEffect(() => {
    const totalQuantity = filteredAndSortedInvoices.reduce(
      (sum, { invoiceQuantity, unit }) => {
        if (unit === selectedProduct?.purchasedUnit) {
          return sum + invoiceQuantity;
        }
        const { Value } = getConvertUnitFromTo(
          invoiceQuantity,
          unit,
          selectedProduct?.purchasedUnit,
          {
            stdpackageunit: selectedProduct?.stdPackageUnit,
            stdunit: selectedProduct?.stdUnit,
            stdfactor: selectedProduct?.stdFactor,
            ...selectedProduct
          },
          convertFn
        );
        return sum + Value;
      },
      0
    );
    setFilteredTotalInvoiceQuantity(totalQuantity);
  }, [filteredAndSortedInvoices, selectedProduct, convertFn]);

  const isTableLoading = !selectedProduct || invsLoading || isLoading;

  const handleSort = (field, dir) => {
    if (isTableLoading) {
      return;
    }
    const _dir = !dir ? 'asc' : dir;
    setSortField(field);
    setSortDir(_dir);
  };

  const renderCell = (field, invoice) =>
    cellRenderers[field](invoice, feInvoicesActive);

  const isEmpty = paginatedRows.length === 0;
  const hasAggregateTotal =
    useProductScopedLookup && totalInvoicedQuantity !== null;
  let aggregateTotalInPurchasedUnit = totalInvoicedQuantity;
  if (
    hasAggregateTotal &&
    (totalInvoicedQuantityUnit || selectedProduct?.purchasedUnit) !==
      selectedProduct?.purchasedUnit
  ) {
    aggregateTotalInPurchasedUnit = getConvertUnitFromTo(
      totalInvoicedQuantity,
      totalInvoicedQuantityUnit || selectedProduct?.purchasedUnit,
      selectedProduct?.purchasedUnit,
      {
        stdpackageunit: selectedProduct?.stdPackageUnit,
        stdunit: selectedProduct?.stdUnit,
        stdfactor: selectedProduct?.stdFactor,
        ...selectedProduct
      },
      convertFn
    ).Value;
  }
  let displayTotalInvoiceQuantity = selectedProduct?.purchased;
  if (dateRange?.length || cropSeasons?.length) {
    displayTotalInvoiceQuantity = filteredTotalInvoiceQuantity;
  }
  if (hasAggregateTotal) {
    displayTotalInvoiceQuantity = aggregateTotalInPurchasedUnit;
  }

  return (
    <div
      className={`product-invoice-table flex min-h-0 ${
        isTableLoading ? 'product-invoice-table--loading' : ''
      }`}
      style={{ height: 'calc(100% - 3rem)' }}
    >
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        <div
          className="product-invoice-table__viewport flex flex-1 min-h-0 flex-col overflow-auto"
          aria-busy={isTableLoading}
        >
          {(isTableLoading || !isEmpty) && (
            <SortableTable
              columns={TABLE_COLUMNS}
              rows={isTableLoading ? [] : paginatedRows}
              renderCell={renderCell}
              sortBy={sortField}
              sortDir={sortDir}
              onSort={handleSort}
            />
          )}
          {isTableLoading && (
            <div
              className="flex flex-1 items-center justify-center"
              data-testid="invoice-table-loading"
            >
              <LoadingSpinner message="" size={75} label="Loading invoices" />
            </div>
          )}
          {!isTableLoading && isEmpty && (
            <ProductDetailTableEmptyState
              type="invoices"
              totalItemCount={invoiceRows.length}
              onButtonClick={() => history.push(routesForPPLToCreate.invoice)}
            />
          )}
        </div>
        {!isTableLoading && filteredAndSortedInvoices.length > 0 ? (
          <div className="relative w-full px-6 border border-t-0 border-neutral-20 shrink-0">
            {/* Banner is placed at a fixed distance from the right edge so the
                pagination's changing label width can't push it around. The
                pagination itself renders naturally, right-aligned. */}
            <div
              className="absolute bg-banner-alert-blue-10 rounded py-1 px-3"
              style={{ right: 210, top: '50%', transform: 'translateY(-50%)' }}
            >
              <span>Total Invoiced Quantity: </span>
              <span className="font-semibold">
                {formatNumber(
                  displayTotalInvoiceQuantity,
                  getAbbreviatedUnit(selectedProduct?.purchasedUnit)
                )}
              </span>
            </div>
            {CwFPagination(
              filteredAndSortedInvoices.length,
              page,
              setPage,
              null,
              PAGE_COUNT
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

ProductDetailInvoiceTable.propTypes = {
  cropSeasons: PropTypes.arrayOf(PropTypes.string).isRequired,
  dateRange: PropTypes.arrayOf(PropTypes.any)
};

export default ProductDetailInvoiceTable;
